import { useEffect } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { LogoBlack } from '../../components/Logo/Logo';

const TermsOfServiceEN = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Box sx={{ bgcolor: "grey.C50", color: "grey.C10", px: { xs: 1, sm: 2, md: 8 }, py: { xs: 3, md: 8 } }}>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 8 } }}>
          <Box sx={{ flex: "0 0 auto", mt: 1 }}>
            <Link component={RouterLink} to="/" underline="none">
              <LogoBlack />
            </Link>
          </Box>
        </Box>

        <Box sx={{ maxWidth: '800px', mx: { xs: 0, md: 8 }, mt: { xs: 1, md: 4 } }}>

          <Typography variant="h1" sx={{ mb: { xs: 2, md: 6 }, fontWeight: 300, color: "greydark.C900", fontSize: { xs: '1.1rem', sm: '2.2rem', md: '3.75rem' } }}>
            Terms of Service
          </Typography>

          <Typography variant="h4" sx={{ lineHeight: { xs: 1.5, md: 1.8 }, opacity: 0.8, fontWeight: 300, color: "greydark.C900", fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem' } }}>
            <strong>Last updated: January 9th, 2026</strong>
            <br /><br />

            <strong>Applicability and governing law</strong>
            <br /><br />
            These Terms and Conditions apply to all contracts concluded through the speak23 platform and services
            (speak23 GmbH, Unterer Schreiberweg 13/3, 1190 Vienna, Austria) between us and you as a customer.
            Our product range is primarily intended for end consumers and for business customers (B2B).
            An end consumer is any natural person who concludes a transaction for private purposes;
            an entrepreneur is a person or company acting in the exercise of their commercial or independent professional activity.
            For consumers with habitual residence in the European Union, Austrian law shall apply,
            without prejudice to mandatory consumer protection provisions of the respective EU country.

            In individual cases, there may be specific terms for partners.
            In all other cases (in particular business transactions or dealings outside the EU),
            the law of the Republic of Austria applies exclusively,
            to the exclusion of the UN Convention on Contracts for the International Sale of Goods (CISG).
            <br /><br />

            <strong>Subject matter of the contract</strong>
            <br /><br />
            speak23 solutions are products and services of speak23 GmbH specialising in preventive health.
            Through our platform www.speak23.com, customers can order products related to preventive health.

            In particular, we offer blood tests in certified laboratories and at-home microbiome analysis kits.
            In case of gut microbiome analysis you send the collected sample in the included return envelope to our partner laboratory,
            where analysis is performed using DNA sequencing. speak23 evaluates the lab data bioinformatically and
            with the help of modern algorithms (including artificial intelligence) and provides you
            based on available information and blood test results with a detailed results report and personalised recommendations on our speak23 platform.

            Please note that speak23 does not provide a medical diagnosis,
            and the analysis report is not a substitute for a doctor's examination.
            Our consultation is limited to scientific analyses and general health and longevity tips;
            for individual medical advice please consult a qualified professional.
            Your sample is analysed in anonymised form (only via the ID) to ensure privacy and confidentiality.

            speak23 and partners have obliged the partner laboratory to comply with all applicable data protection regulations.

            Additionally, we offer other products to promote preventive health.
            <br /><br />

            <strong>User profile</strong>
            <br /><br />
            You need a permanent user account to order and leverage our products and recommendations;
            The onboarding process includes user registration and authentication, subscription membership advance
            the collection of required personal data used for personalized recommendations.
            By signing in with provided user credentials the user gets access to all the information, test results and recommendations.

            The access credentials for your user profile must be kept confidential.
            Please ensure that no unauthorised third party gains access to any emails we send you or your password.
            speak23 is not liable for any damages arising from unauthorised account access,
            provided you have caused them by not taking adequate care in securing your access data.

            Your analysis results and personal data are stored in your profile in accordance with our Privacy Policy.
            You may request deletion of your profile and data at any time, provided there are no legal retention obligations preventing this.

            Please send your request by email to office@speak23.com; we will then immediately delete or block your data.
            Please ensure that you provide complete and correct personal information when you do onboarding or use any services.
            You must be at least 18 years old to order a speak23 test or to set up a user profile.
            <br /><br />

            <strong>Conclusion of contract</strong>
            <br /><br />
            The presentation of our goods and services in the online shop does not yet constitute a legally binding offer,
            but rather an invitation for you to place an order (invitatio ad offerendum).
            You submit a legally binding purchase offer (your order) by clicking on "Secure Checkout" in the ordering process.
            Immediately after submitting the order you will receive an automated order confirmation by email,
            which acknowledges receipt of your order. This email does not yet constitute acceptance of your offer.
            The purchase contract is only concluded when we accept your offer explicitly -
            either by a further email (shipping confirmation or order confirmation) or by delivery of the goods.

            Please also check your spam folder regularly, as our emails may be delivered there.
            If speak23 GmbH does not accept your offer (e.g. because a product is not available),
            we will inform you immediately by email.

            The languages available for the conclusion of the contract are German and English.
            <br /><br />

            <strong>Order process / Formation of contract</strong>
            <br /><br />
            In the speak23 shop or configurator you can select products/tests by adding them to the shopping cart.
            You can view and change the contents of the cart at any time.
            When you want to complete the order, click "Secure Checkout" and follow the further instructions in the checkout process.
            You then enter the required billing and delivery information and select the desired shipping and payment method.
            Before submitting the order, all order data and costs are summarised in an order overview.
            Please check this overview carefully. You can correct input errors until final submission of the order
            or cancel the ordering process at any time.

            By clicking on "Secure Checkout" you finally submit your binding purchase offer for the listed products.
            After submission, you will receive our order confirmation by email.
            The final acceptance of the contract occurs through our shipping/order confirmation by email or at the latest by delivery of the goods to you.

            speak23 GmbH reserves the right not to accept orders in individual cases - there is no claim to conclusion of a contract.
            In such a case, we will inform you immediately and refund any payments already received.
            We recommend that you save the order confirmation/invoice as well as these Terms and Conditions for your records.
            <br /><br />

            <strong>Subscriptions</strong>
            <br /><br />
            As prerequisite for one-time purchases (add-on tests), we offer subscriptions (recurring orders) for speak23 membership and core health testing.
            When you place a subscription order, you receive the chosen product at regular intervals specified by you (e.g. bi-annual or quarterly testing in the
            selected certified laboratory).

            Payment is due upon ordering with annual billing; Your payment data may be stored securely for this purpose,
            to allow recurring charges. You have the right to change, pause or cancel your subscription at any time,
            as long as we have not yet dispatched any part of the next delivery or provided any corresponding service.
            Changes or cancellations can be made through our customer service.
            If you cancel a subscription after part of your order has already been dispatched or a partial service has been provided,
            we will refund you the amount already paid, minus the reasonable costs incurred by us for the already completed (partial) delivery.
            This means you only pay for products/services that you have actually received up to the cancellation date.
            If a subscription with a fixed term was agreed, it will automatically renew for the same term unless you cancel in due time before its expiry.
            <br /><br />

            <strong>Right of withdrawal (cancellation)</strong>
            <br /><br />
            Consumers, as defined by the Austrian Consumer Protection Act (KSchG),
            have the statutory right to withdraw from the contract within 14 days without giving any reason.

            The withdrawal period is 14 days from the day on which you (or a third party named by you, who is not the carrier) took possession of the goods.
            Once a digital service has been received or consumed, it can no longer be returned.

            Likewise, if a test has been subsequently upgraded and the results have already been provided, no return is possible.
            Furthermore, if a consultation appointment has been booked and a date has been set, that service cannot be cancelled.

            To exercise your right of withdrawal, you must inform us (speak23 GmbH, Unterer Schreiberweg 13/3, 1190 Wien, Austria, email: office@speak23.com) of your decision
            to withdraw by means of a clear statement (e.g. a letter sent by post or email).

            To meet the withdrawal deadline it is sufficient that you send your communication before the 14-day period expires.
            Consequences of withdrawal: If you validly withdraw from the contract, we will refund you all payments received from you,
            except for any handling fees. We will make the refund using the same payment method that you used for the original transaction,
            unless expressly agreed otherwise. You will incur no fees for the refund.

            Exclusions: The right of withdrawal does not apply to goods sealed for health or hygiene reasons which are not suitable for return
            if unsealed after delivery. This specifically applies to speak23 test kits that have already been opened or used,
            as their contents cannot be reused for health reasons.

            Please note: The right of withdrawal under this section applies only to consumers (not to business customers).
            <br /><br />

            <strong>Payment and retention of title</strong>
            <br /><br />
            The prices shown in our online platform are total prices and include the statutory VAT.
            Any additional fees or shipping costs will be shown separately during the ordering process, if any.
            Before completing the order, the total gross price of your shopping cart, including all costs, will be clearly displayed.

            speak23 GmbH offers various payment methods (e.g. credit card, PayPal, etc.),
            but is not obliged to offer any specific payment methods indefinitely.
            No additional fees are charged by speak23 GmbH for using the offered payment methods.

            Payment is due in full at the time of purchase (plus any shipping costs).
            If you default on payment, we are entitled to charge interest on arrears at the statutory rate.
            The delivered goods remain the property of speak23 GmbH until full payment has been made (retention of title).
            <br /><br />

            <strong>Warranty</strong>
            <br /><br />
            The statutory provisions on warranty apply to our goods and services.
            For consumer transactions, the warranty period for new goods is two years from delivery of the goods.
            For orders by entrepreneurs (B2B), the warranty period for movable items is limited to one year from delivery of the goods,
            insofar as legally permissible.
            Should a delivered product have defects, consumers are entitled to statutory warranty claims
            (repair/completion, replacement, reduction in price, or rescission in accordance with §§ 922 ff ABGB).

            Please notify us of obvious defects as soon as possible and
            do not return the goods to us in case of a complaint without first contacting our customer service.
            Any manufacturer's guarantees remain unaffected and apply in addition under the respective guarantee conditions.

            For entrepreneurs, warranty claims are subject to the obligation to inspect and give notice under § 377 UGB.
            Complaints can be made to us at the contact details given in the imprint.
            <br /><br />

            <strong>Liability and disclaimer</strong>
            <br /><br />
            speak23 GmbH performs all services with the greatest care according to the current state of the art
            and endeavours to ensure high availability of our online offerings.

            However, we reserve the right to change, restrict or discontinue functions of the website or offered services if necessary
            (e.g. for maintenance purposes). Temporary restrictions in availability do not entitle you to claims for damages.

            Technical limitation of liability: speak23 GmbH assumes no liability for technical disruptions beyond our control,
            in particular not for unintentional interruptions, failures, data loss or transmission errors in connection with the use of our online offerings.
            In particular, we are not liable for delays or performance failures due to force majeure
            (e.g. natural disasters, strikes, war, government interventions) or other unforeseeable events for which we are not responsible.
            Maintenance work may cause temporary limited availability; this does not entitle customers to any claims.

            General liability: In cases of slight negligence, we are not liable - except in the event of personal injury - for financial losses.
            For damages caused by us or our agents through gross negligence or intent, we are in principle fully liable.
            The above liability limitations apply correspondingly to persons associated with speak23 GmbH as well as our employees and vicarious agents.
            Mandatory liability provisions remain unaffected, in particular liability for personal injury and liability under the Product Liability Act.

            Products and health instructions: Some products offered on our website are dietary supplements intended to supplement the diet
            with certain nutrients. Dietary supplements are not a substitute for a varied, balanced diet and a healthy lifestyle.
            When used as directed, they generally have no undesirable side effects in healthy adults.
            Should there nevertheless be side effects or health issues due to individual intolerances, speak23 GmbH assumes no liability.
            <br /><br />

            <strong>Indemnification</strong>
            <br /><br />
            You, as the customer, undertake to indemnify and hold speak23 GmbH harmless in the event that we are subject to claims
            by third parties as a result of your conduct. This applies in particular to cases where third-party claims arise from the fact that you:

            (a) entered, published or transmitted illegal or defective content in our system;
            (b) used our website or services in an unlawful manner;
            (c) violated these Terms or any applicable site rules;
            (d) infringed the rights of third parties (persons or organisations); or
            (e) introduced harmful programs such as viruses or trojans into our systems.

            In all these cases, you will compensate speak23 GmbH for all resulting damages and expenses and
            will hold us harmless against claims by third parties.
            <br /><br />

            <strong>Final provisions</strong>
            <br /><br />
            The law of the Republic of Austria shall apply to this contractual relationship - subject to mandatory international consumer protection laws -
            to the exclusion of the conflict-of-law rules of international private law and the UN Convention on Contracts for the International Sale of Goods.

            The exclusive place of jurisdiction for all disputes arising from or in connection with this contract shall be the competent court in Vienna,
            unless mandatory consumer protection regulations to the contrary apply.

            The language of the contract is German (translated versions are for informational purposes only).
            Should any provision of these Terms be or become invalid or unenforceable, the remaining provisions shall remain in force.
            In place of the invalid clause, the relevant statutory provision shall apply. The same applies to any gaps.

            The European Commission provides a platform for online dispute resolution at http://ec.europa.eu/consumers/odr.
            Consumers have the opportunity to use this platform to resolve disputes arising from online purchase contracts.
            However, please note that we are neither obliged nor willing to participate in a dispute resolution procedure before a consumer arbitration board.

            You can also send complaints directly to us (Email: office@speak23.com); we will endeavour to find an amicable solution.

            Links to external websites or information sources on our website are intended as a service for customers.
            Despite careful selection and regular review, we assume no responsibility for the content of external links (see imprint).
            If linked content is questionable, please inform us so that we can remove the link.

            All trademarks, logos, graphics and texts on our website are the intellectual property of speak23 GmbH
            (or the respective rights holders) and are protected by copyright and trademark laws.
            Any use by third parties is not permitted without our prior written consent.

            speak23 GmbH reserves the right to change these Terms for the future, provided the changes are minor, objectively justified and
            reasonable for consumers. We will inform you of planned changes by email or a pop-up on the platform.

            Every customer has the right to object to changes within one month of receiving notification of them.
            If you do not object in due time, the changed Terms will be deemed accepted.
            We will specifically draw your attention to this consequence in the notification of changes.
            If you object in due time, we reserve the right to terminate the contract properly.

            By placing an order, the customer agrees to these Terms and Conditions.
            The current version of the Terms and Conditions can be accessed at any time at www.speak23.com.
            We recommend downloading or printing these Terms at the time of contract conclusion and keeping them for your records.
          </Typography>
        </Box>

      </Box>
    </>
  );
};

export default TermsOfServiceEN;
